import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/Login.vue'),
      meta: { requiresAuth: false, layout: 'blank' }
    },
    {
      path: '/sso/callback',
      name: 'auth-callback',
      component: () => import('@/pages/AuthCallback.vue'),
      meta: { requiresAuth: false, layout: 'blank' }
    },
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/pages/Dashboard.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/templates',
      name: 'templates',
      component: () => import('@/pages/Templates.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/motifs',
      name: 'motifs',
      component: () => import('@/pages/Motifs.vue'),
      meta: { requiresAuth: true, requiresScope: 'motifs:read' }
    },
    {
      path: '/motifs/:code',
      name: 'motif-authoring',
      component: () => import('@/pages/MotifAuthoring.vue'),
      meta: { requiresAuth: true, requiresScope: 'motifs:read' }
    },
    {
      path: '/presentations',
      name: 'presentations',
      component: () => import('@/pages/Presentations.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/workflows',
      name: 'workflows',
      component: () => import('@/pages/Workflows.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/collections',
      name: 'collections',
      component: () => import('@/pages/Collections.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      component: () => import('@/pages/settings/SettingsLayout.vue'),
      meta: { requiresAuth: true },
      redirect: '/settings/profile',
      children: [
        {
          path: 'profile',
          name: 'settings-profile',
          component: () => import('@/pages/settings/ProfileSettings.vue'),
        },
        {
          path: 'api-keys',
          name: 'settings-api-keys',
          component: () => import('@/pages/settings/ApiKeysSettings.vue'),
        },
        {
          path: 'users',
          name: 'settings-users',
          component: () => import('@/pages/settings/UsersSettings.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'groups',
          name: 'settings-groups',
          component: () => import('@/pages/settings/GroupsSettings.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'roles',
          name: 'settings-roles',
          component: () => import('@/pages/settings/RolesSettings.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'scopes',
          name: 'settings-scopes',
          component: () => import('@/pages/settings/ScopesSettings.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'executions',
          name: 'settings-executions',
          component: () => import('@/pages/settings/ExecutionsSettings.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'system',
          name: 'settings-system',
          component: () => import('@/pages/settings/SystemSettings.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'motifs',
          name: 'settings-motifs',
          component: () => import('@/pages/settings/MotifSettings.vue'),
          meta: { requiresScope: 'motifs:approve' },
        },
        {
          path: 'motif-categories',
          redirect: { name: 'settings-motifs', hash: '#motif-categories-title' },
        },
        {
          path: 'motif-naming-lists',
          redirect: { name: 'settings-motifs', hash: '#naming-list-kinds-title' },
        },
        {
          path: 'about',
          name: 'settings-about',
          component: () => import('@/pages/settings/AboutSettings.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      redirect: '/'
    }
  ]
})

// Navigation guard for authentication
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Check if route requires authentication
  const requiresAuth = to.meta.requiresAuth !== false

  if (requiresAuth && !authStore.isAuthenticated) {
    // Try to validate existing token
    if (authStore.accessToken) {
      try {
        await authStore.checkAuth()
        if (authStore.isAuthenticated) {
          // Continue to admin check below
        } else {
          next({
            name: 'login',
            query: { redirect: to.fullPath }
          })
          return
        }
      } catch {
        next({
          name: 'login',
          query: { redirect: to.fullPath }
        })
        return
      }
    } else {
      next({
        name: 'login',
        query: { redirect: to.fullPath }
      })
      return
    }
  }

  // Check admin-only routes
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    next({ name: 'settings-profile' })
    return
  }

  // Check routes that ask for a single scope. Kept general rather than motif-specific: same
  // shape as the admin branch above, and the next scope-bound page needs no change here.
  // A global beforeEach runs before the page component is loaded, so no request goes out.
  const requiredScope = to.meta.requiresScope as string | undefined
  if (requiredScope && !authStore.hasScope(requiredScope)) {
    next({ name: 'dashboard' })
    return
  }

  // If authenticated user tries to access login page, redirect to dashboard
  if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
    return
  }

  next()
})

export default router
